import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { RoleRow } from "@/features/settings/use-roles";
import { useInternalUsers } from "../use-internal-users";
import { useAssignRole } from "@/features/settings/use-assign-role";

// SPA port of /app/admin/settings/roles/[id]/_components/AssignMembersTab.tsx.
// Lists every internal profile and renders a Select of all roles per row.
// Changing the select fires useAssignRole; per-row pending state is
// derived from the mutation's `variables` so multiple users can be in
// flight independently.

type Props = {
  roles: RoleRow[];
  canManage: boolean;
};

export function AssignMembersTab({ roles, canManage }: Props) {
  const usersQuery = useInternalUsers();
  const mutation = useAssignRole();

  const [query, setQuery] = useState("");
  const profiles = usersQuery.data ?? [];

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return profiles;
    return profiles.filter((p) => {
      const name = (p.full_name ?? "").toLowerCase();
      const email = (p.email ?? "").toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [profiles, query]);

  const roleNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of roles) map.set(r.id, r.name);
    return map;
  }, [roles]);

  const pendingUserId =
    mutation.isPending && mutation.variables ? mutation.variables.userId : null;

  return (
    <div className="space-y-3">
      <div className="relative max-w-sm">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name or email…"
          className="h-9 pl-9"
        />
      </div>

      {!canManage && (
        <p className="rounded-md border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
          Read-only — requires <code className="rounded bg-muted px-1 py-0.5 font-mono">roles.manage</code> to change assignments.
        </p>
      )}

      <ul className="divide-y rounded-md border bg-card">
        {usersQuery.isLoading && (
          <li className="px-4 py-6 text-center text-sm text-muted-foreground">
            Loading…
          </li>
        )}
        {!usersQuery.isLoading && filtered.length === 0 && (
          <li className="px-4 py-6 text-center text-sm text-muted-foreground">
            No internal users match this search.
          </li>
        )}
        {filtered.map((profile) => {
          const isPending = pendingUserId === profile.user_id;
          const currentRoleName = profile.role_id
            ? roleNameById.get(profile.role_id) ?? "Unknown role"
            : "No role";
          return (
            <li
              key={profile.user_id}
              className="flex items-center justify-between gap-3 px-4 py-2.5"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">
                  {profile.full_name?.trim() || profile.email || profile.user_id}
                </p>
                <p className="truncate text-xs text-muted-foreground">
                  {profile.email ?? "—"}
                  <span className="ml-2 text-muted-foreground/70">·</span>
                  <span className="ml-2 text-muted-foreground/90">
                    {currentRoleName}
                  </span>
                </p>
              </div>
              <div className="w-48">
                <Select
                  value={profile.role_id ?? undefined}
                  onValueChange={(v) =>
                    mutation.mutate({ roleId: v, userId: profile.user_id })
                  }
                  disabled={!canManage || isPending}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder="Assign role…" />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map((r) => (
                      <SelectItem key={r.id} value={r.id} className="text-xs">
                        {r.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
